import type { ComponentType } from 'react';
import { useAuthStore } from '@indipia/shared';
import { EmptyState, IconWarningCircle, Page } from '@indipia/ui-core';

function OwnerOnlyNotice() {
  return (
    <Page width="md">
      <EmptyState
        className="mt-08"
        icon={<IconWarningCircle />}
        title="사장님만 이용할 수 있어요"
        description="구독·결제 관리는 사장님 계정으로만 가능해요. 궁금한 점은 사장님께 문의해주세요."
      />
    </Page>
  );
}

/**
 * 구독·결제 관리는 사장님(마스터) 계정만 이용할 수 있다(사용자 결정, 2026-09-08 — "구독 결제는
 * 사장님만 가능"). shell-web RequireAuth(로그인 여부)를 이미 통과한 뒤에만 렌더되므로, 여기서는
 * 그 위에 masterYn만 한 번 더 확인한다 — 직원 계정이 메뉴에 안 보여도 URL을 직접 쳐서 들어오는
 * 경로까지 막기 위함(billing agent의 manifest.permissions는 shell-web 어디서도 실제로 소비되지
 * 않는 문서용 값이라 이 화면 자체에서 한 번 더 걸러야 한다).
 * <p>
 * 라우트 정의 표준(D-02 §4.1.6, packages/shared/src/agents/contract.ts의 validateRegistry)이
 * "Component 참조형만 허용, element/레이아웃 라우트(children으로 감싸기) 금지"를 강제해서, 부모
 * 라우트로 감싸는 대신 각 페이지 컴포넌트를 이 HOC로 감싸 routes.ts의 Component 자리에 그대로
 * 넣는다(예: {@code Component: withOwnerGuard(PricingPlansPage)}) — routes.ts 참고.
 * <p>
 * BE(sv-billing)도 동일 기준(X-Roles 헤더에 OWNER 포함 여부)으로 재검증한다 — 이 HOC는 UX용일
 * 뿐 보안 경계가 아니다(C-05 §10 원칙, 어차피 axios 401/403 인터셉터가 BE 거부 응답도 처리함).
 */
export function withOwnerGuard<P extends object>(PageComponent: ComponentType<P>) {
  return function OwnerGuarded(props: P) {
    const isOwner = useAuthStore((s) => s.user?.masterYn === 'Y');
    if (!isOwner) return <OwnerOnlyNotice />;
    return <PageComponent {...props} />;
  };
}
